import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as UploadIcon} from '../../../../assets/images/upload.svg';
import ArrBarChartSelectable, {
  BarChartSelectableSeries,
} from '../../../common/arr-bar-chart-selectable/ArrBarChartSelectable';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import {useDispatch, useSelector} from 'react-redux';
import {CustomMenuItem} from '../../../common/arr-menu/ArrMenu';
import {RootState} from '../../../../redux/store';
import {ITallyResultResponseBody} from '../../../../api/types/reportingApi.type';
import {useTallyExport} from '../../../../hooks/reporting/useReporting';
import {
  getTallActionPAU,
  getTallyActionRMU,
  convertToIsoFormat,
  handleExportUtil,
} from '../../../../utils/reportUtils';
import {
  SourceTypeCode,
  SourceTypeContextCode,
  ToastType,
} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {
  setActiveActionContext,
  updateTally,
} from '../../../../redux/slice/tallyReportSlice';
import TallyActions from '../tally-actions/TallyActions';

export type TallyResultProps = {
  isTallyRun?: boolean | ITallyResultResponseBody;
  barResult?: (value: any) => void;
  handleLoading?: (value: boolean) => void;
  asOfText?: string;
  preSelectedBars?: string[];
};

const roles = {
  systemAdmin: 'System Administrator',
  domainAdmin: 'Domain Administrator',
  reviewManager: 'Review Manager',
  reviewer: 'Reviewer',
  projectAdmin: 'Project Administrator',
};

const formatUtcAsOf = (dt: Date): string => {
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mi = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss} UTC`;
};

const TallyResult: React.FC<TallyResultProps> = ({
  isTallyRun,
  handleLoading,
  barResult,
  asOfText,
  preSelectedBars = [],
}) => {
  const tallyEnabled = useMemo(() => {
    return Boolean(isTallyRun);
  }, [isTallyRun]);
  const noTallyData =
    typeof isTallyRun === 'boolean'
      ? isTallyRun
      : !isTallyRun?.tallyByCriteriaList?.length;
  const [barLabels, setBarLabels] = useState<string[]>(['']);
  const [barSeries, setBarSeries] = useState<BarChartSelectableSeries[]>([]);
  const computedAsOf = useMemo(() => formatUtcAsOf(new Date()), [isTallyRun]);
  const resolvedAsOf = asOfText ?? computedAsOf;

  const [isActionBtnDisabled, setIsActionBtnDisabled] = useState(false);
  const [isUploadBtnDisabled, setIsUploadBtnDisabled] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const {mutateAsync: getTallyExportReport} = useTallyExport();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuItem, setMenuItem] = useState<CustomMenuItem[]>(
    getTallActionPAU(t)
  );
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const tallyResponse = useSelector(
    (state: RootState) => state.tallyreport.tallyResponse
  );
  const selectedSource = useSelector(
    (state: RootState) => state.tallyreport.selectedSource
  );
  const tallyData = useSelector(
    (state: RootState) => state.tallyreport.tallyData
  );

  const tallyResult = useSelector(
    (state: RootState) => state.tallyreport.tallyResult
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (loginUserData?.roleName === roles.projectAdmin) {
      setMenuItem(getTallActionPAU(t));
    } else if (loginUserData?.roleName === roles.reviewManager) {
      setMenuItem(getTallyActionRMU(t));
    }
  }, [loginUserData?.roleName]);

  const [selectedBars, setSelectedBars] = useState<string[]>([]);

  const getBarKey = useCallback((seriesIdx: number, dataIdx: number) => {
    return `${seriesIdx}-${dataIdx}`;
  }, []);

  useEffect(() => {
    if (tallyEnabled) {
      if (typeof isTallyRun !== 'boolean' && isTallyRun?.tallyByCriteriaList) {
        dispatch(
          updateTally({
            tallyResult: {
              dataasof: convertToIsoFormat(computedAsOf),
              list: isTallyRun.tallyByCriteriaList,
              docs: [],
            },
          })
        );
        const labels = isTallyRun.tallyByCriteriaList.map(
          (item) => item.fieldValue
        );
        setBarLabels(labels);
        const series = {
          label: '',
          data: isTallyRun?.tallyByCriteriaList?.map((item) => item.docCount),
        };
        setBarSeries([series]);
      } else {
        setBarLabels([]);
        setBarSeries([]);
      }
    } else {
      setBarLabels([]);
      setBarSeries([]);
    }
  }, [JSON.stringify(isTallyRun)]);

  const allBarKeys = useMemo(() => {
    return barSeries.flatMap((s, seriesIdx) =>
      s.data.map((_, dataIdx) => getBarKey(seriesIdx, dataIdx))
    );
  }, [barSeries, getBarKey]);

  useEffect(() => {
    if (tallyEnabled && allBarKeys.length > 0 && preSelectedBars?.length) {
      const preselectedKeys = new Set<string>();

      preSelectedBars.forEach((lbl) => {
        barLabels.forEach((labelAtIdx, dataIdx) => {
          if (labelAtIdx === lbl) {
            barSeries.forEach((_, seriesIdx) => {
              preselectedKeys.add(getBarKey(seriesIdx, dataIdx));
            });
          }
        });
      });

      setSelectedBars(Array.from(preselectedKeys));
    }
  }, [tallyEnabled, allBarKeys, preSelectedBars]);

  const isIndeterminate =
    selectedBars.length > 0 && selectedBars.length < allBarKeys.length;

  const allSelected = !noTallyData && selectedBars.length === allBarKeys.length;

  const isDisabled = !tallyEnabled;

  useEffect(() => {
    if (isDisabled) {
      setSelectedBars([]);
      setIsMenuOpen(false);
    }
  }, [isDisabled]);

  useEffect(() => {
    if (selectedBars?.length >= 1) {
      setIsActionBtnDisabled(false);
      setIsUploadBtnDisabled(false);
    } else {
      setIsActionBtnDisabled(true);
      setIsUploadBtnDisabled(true);
    }
  }, [selectedBars]);

  useEffect(() => {
    if (barResult) {
      const selectedBarDetails = selectedBars.map((key) => {
        const [seriesIdx, dataIdx] = key.split('-').map(Number);
        return {
          label: barLabels[dataIdx],
          value: barSeries[seriesIdx].data[dataIdx],
        };
      });
      const tallyIdData = tallyData?.idLabels;
      const merged = selectedBarDetails.map((item: any) => {
        const match = tallyIdData.find((x: any) => x.Label === item.label);
        return match ? {...item, id: match.Id} : item;
      });

      dispatch(
        updateTally({
          tallyResult: {
            dataasof: convertToIsoFormat(computedAsOf),
            list:
              typeof isTallyRun !== 'boolean' && isTallyRun?.tallyByCriteriaList
                ? isTallyRun?.tallyByCriteriaList
                : [],
            docs: merged,
          },
        })
      );
      barResult(selectedBarDetails);
    }
  }, [selectedBars]);

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleBarClick = useCallback(
    (_barKey: string, _barData: {label: string; value: number}) => {
      if (isDisabled) return;
    },
    [isDisabled, selectedBars]
  );

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      if (isDisabled) return;
      setSelectedBars(checked ? allBarKeys : []);
    },
    [isDisabled, allBarKeys]
  );
  const now = new Date();

  const dateTime = `${now.getUTCFullYear()}.${String(now.getUTCMonth() + 1).padStart(2, '0')}.${String(now.getUTCDate()).padStart(2, '0')} ${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}:${String(now.getUTCSeconds()).padStart(2, '0')}`;
  const handleUploadClick = useCallback(async () => {
    const sourceData = tallyResult?.list
      .filter((l: any) =>
        tallyResult?.docs.some((d: any) => d.label === l.fieldValue)
      )
      .map(({fieldValue, docCount, docVolume}: any) => ({
        FieldValue: fieldValue,
        DocCount: docCount,
        DocVolume: docVolume,
      }));
    const tallySelectType = `${tallyData?.label} : ${tallyData?.idLabels.map((item: any) => item.Label).join(',')}`;
    const filterTallySelectionsView = Object.values(tallyData?.filters)
      .map((item: any) => {
        if (!item?.label) return null;
        const selectionType = item.selectionType
          ? item.selectionType.charAt(0).toUpperCase() +
            item.selectionType.slice(1)
          : '';
        if (item?.type === 'INT') {
          return `${item.label}: ${selectionType} ${item?.startSize} ${selectionType === 'Between' ? `to ${item?.endSize}` : ''}`;
        }
        if (item?.type === 'DATETIME') {
          return `${item.label}: ${selectionType} ${item?.startDate} ${selectionType === 'Between' ? `to ${item?.endDate}` : ''}`;
        }
        const values =
          item.values?.map((v: any) => `"${v.text}"`).join(',') || '';

        return `${item.label} - ${selectionType}: ${values}`;
      })
      .filter(Boolean)
      .join(',');
    handleLoading?.(true);
    const {success, data} = await handleExportUtil({
      tallyResponse,
      sourceData,
      tallySelectType,
      selectedSource,
      filterTallySelectionsView,
      dateTime,
      getTallyExportReport,
    });

    if (success && data) {
      handleLoading?.(false);
      triggerToast(t('reports.common.reportExportSuccess'), [
        ToastType.Success,
      ]);
    } else {
      handleLoading?.(false);
      triggerToast(t('reports.common.reportExportError'), [ToastType.Error]);
    }
  }, [
    getTallyExportReport,
    isDisabled,
    tallyResult,
    tallyResponse,
    tallyData,
    selectedSource,
  ]);

  const minHeight = 150;
  const extraHeightPerLabel = 20;
  const chartHeight = minHeight + barLabels.length * extraHeightPerLabel;

  return (
    <Box data-testid="tally-result-panel" className="tally-result-panel" tabIndex={0}>
      <div
        className="tally-result-toolbar"
        data-testid={`tally-result-data-as-of-select-all`}
      >
        <div
          className="tally-result-time-section"
          data-testid={`tally-result-data-as-of`}
        >
          <Typography
            className="typo-text body4_size"
            aria-label={t('reports.tally.tallyResult.dataAsOf', {
              defaultValue: 'Data as of:',
            })}
            data-testid={`tally-result-data-as-of-text`}
            tabIndex={0}
          >
            {t('reports.tally.tallyResult.dataAsOf', {
              defaultValue: 'Data as of:',
            })}
            {tallyEnabled && (
              <span
                className="asof-date"
                aria-label={resolvedAsOf}
                data-testid={`tally-result-span`}
                tabIndex={0}
              >
                {' '}
                {resolvedAsOf}
              </span>
            )}
          </Typography>
        </div>

        <div
          className="header-button-div"
          aria-disabled={isDisabled || noTallyData}
          data-testid={`tally-result-select-all`}
        >
          <div
            className="select-all-div"
            data-testid={`tally-result-select-all-wrapper`}
            tabIndex={0}
          >
            <ArrCheckbox
              checked={allSelected && !isDisabled}
              isIndeterminate={isIndeterminate}
              onChange={handleSelectAllChange}
              size="medium"
              disabled={isDisabled || noTallyData}
              aria-label={t('reports.tally.tallyResult.selectAll', {
                defaultValue: 'Select all',
              })}
              data-testid="tallyresult-selectall"
            />
            <Typography
              className="typo-text body3_size"
              aria-label={t('reports.tally.tallyResult.selectAll', {
                defaultValue: 'Select all',
              })}
              data-testid={`tally-result-select-all-text`}
              tabIndex={0}
            >
              {t('reports.tally.tallyResult.selectAll', {
                defaultValue: 'Select all',
              })}
            </Typography>
          </div>

          <ArrIconButton
            icon={
              <UploadIcon
                className={`${isDisabled || noTallyData || isUploadBtnDisabled ? 'inactive-upload-icon' : 'upload-icon'}`}
              />
            }
            onClick={handleUploadClick}
            ariaLabel={t('reports.tally.tallyResult.upload', {
              defaultValue: 'upload',
            })}
            shape={
              isDisabled || noTallyData || isUploadBtnDisabled
                ? 'none'
                : 'default'
            }
            size="medium"
            className="upload-icon-btn"
            testId="upload-button"
            disabled={isDisabled || noTallyData || isUploadBtnDisabled}
            tabIndex={0}
          />

          <Box className="table-header-section" tabIndex={0}>
            <TallyActions
              actions={menuItem}
              isDisabled={isDisabled || noTallyData || isActionBtnDisabled}
              sourceType={SourceTypeCode.TALLY}
              dataTestId="tally-action-menu"
              direction="bottom-right"
              sourceTypeContext={SourceTypeContextCode.TALLY}
              onActionClick={() =>
                dispatch(setActiveActionContext(SourceTypeCode.TALLY))
              }              
            />
          </Box>
        </div>
      </div>
      <div className="bar-chart-main" data-testid={`tally-result-div`}>
        {tallyEnabled && !noTallyData ? (
          <div
            style={{height: `${chartHeight}px`}}
            data-testid={`tally-result-tally-chart-wrap`}
            tabIndex={0}
          >
            <ArrBarChartSelectable
              labels={barLabels}
              series={barSeries}
              xAxisLabel=""
              yAxisLabel=""
              horizontal
              selectedBars={selectedBars}
              onBarClick={handleBarClick}
              onSelectionChange={setSelectedBars}
              multiSelect
              showLegend={false}
              showSelectAllCheckbox={false}
              dataTestId="tally-result-bar-chart"
            />
          </div>
        ) : (
          <Box
            className="tally-empty-state"
            role="status"
            aria-live="polite"
            data-testid="tally-empty-state"
          >
            <Typography
              className="empty-text typo-text body3_size"
              aria-label={
                noTallyData
                  ? t('reports.tally.tallyResult.noData', {
                      defaultValue: 'Data not available',
                    })
                  : t('reports.tally.tallyResult.runTallyMessage', {
                      defaultValue: 'Run Tally to view the results',
                    })
              }
              data-testid={`tally-result-no-tally-dat`}
              tabIndex={0}
            >
              {noTallyData
                ? t('reports.tally.tallyResult.noData', {
                    defaultValue: 'Data not available',
                  })
                : t('reports.tally.tallyResult.runTallyMessage', {
                    defaultValue: 'Run Tally to view the results',
                  })}
            </Typography>
          </Box>
        )}
      </div>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default TallyResult;
